import { KeyboardEvent, useEffect, useMemo, useState } from "react";

import { Button, Checkbox, Form, Icon, Input, Label, Modal } from "semantic-ui-react";

import moment from "moment";

import TimeComboBoxStatic from "../common/TimeComboBoxStatic";

import {
  calculateSlotInfo,
  DSBDetailType,
  GroupedScheduleEventType,
  timeToDecimal,
  UpdateDSBParamsType,
} from "./sequence/ScheduleSetting";

const IMAGES = {
  calendar: "/static/images/schedule/calendar-pink-icon.png",
  provider: "/static/images/schedule/provider-icon.png",
};

type ScheduleFormData = Omit<UpdateDSBParamsType, "id">;

export type ModEditScheduleProps = {
  dsb: DSBDetailType;
  event: GroupedScheduleEventType;
  open: boolean;
  onClose: () => void;
  onUpdateDSB: (data: UpdateDSBParamsType) => Promise<void>;
};

const ModEditSchedule = (props: ModEditScheduleProps) => {
  const [formData, setFormData] = useState<ScheduleFormData>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (props.open) {
      setFormData({
        end_time: props.dsb.doctor_end_time,
        inactive: props.dsb.doctor_dsb_status === 2,
        slot_length: props.dsb.doctor_dsb_slot_length,
        start_time: props.dsb.doctor_start_time,
      });
    }
  }, [props.dsb, props.open]);

  const slotInfo = useMemo(() => calculateSlotInfo(props.dsb), [props.dsb]);

  const handleSave = async (deleted = false) => {
    setIsLoading(true);

    await props.onUpdateDSB({
      id: props.dsb.doctor_dsb_id,
      ...formData,
      date: props.dsb.date,
      deleted,
    });

    setIsLoading(false);

    props.onClose();
  };

  const handleDelete = () => {
    void handleSave(true);
  };

  const handleChangeValue = (field: keyof ScheduleFormData, value: boolean | string) => {
    setFormData((prev) => ({ ...prev, [field]: value as any }));
  };

  const handleChangeTimeSlot = (field: "end_time" | "start_time") => (time: string) => {
    const selectedTime = moment(time, "HH:mm");
    const startTime = moment(props.dsb.doctor_dsb_start_time, "HH:mm");
    const endTime = moment(props.dsb.doctor_dsb_end_time, "HH:mm");

    // ตรวจสอบว่าเวลาที่เลือกอยู่ในช่วงหรือไม่
    if (selectedTime.isBetween(startTime, endTime, null, "[]")) {
      handleChangeValue(field, time);
    } else {
      handleChangeValue(field, props.dsb[`doctor_dsb_${field}`]);
    }
  };

  return (
    <Modal
      style={{
        border: "1px solid rgba(243, 130, 160, 1)",
        borderRadius: "6px",
        minWidth: "32rem",
        width: "max-content",
      }}
      open={props.open}
      closeOnDimmerClick
      onClose={props.onClose}
    >
      <div style={{ padding: "1rem" }}>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Icon
            style={{ color: "rgba(79, 79, 79, 1)", fontSize: "1.15rem", marginBottom: "0.5rem" }}
            name="close"
            link
            onClick={props.onClose}
          />
        </div>

        <Form className="--override-disabled --grey" style={{ padding: "0 0.5rem" }}>
          <Form.Group style={{ alignItems: "center", marginBottom: "1.5rem" }}>
            <Form.Field>
              <img style={{ height: "20px", width: "20px" }} alt="provider" src={IMAGES.provider} />
            </Form.Field>
            <Form.Field>
              <span style={{ color: "rgba(79, 79, 79, 1)", fontWeight: "500" }}>
                {props.dsb.doctor_provider_name}
              </span>
            </Form.Field>
          </Form.Group>

          <Form.Group style={{ alignItems: "center", marginBottom: "1.5rem" }}>
            <Form.Field>
              <img style={{ height: "20px", width: "20px" }} alt="calendar" src={IMAGES.calendar} />
            </Form.Field>
            <Form.Field>
              <span>Appointment</span>
            </Form.Field>
            <Form.Field>
              <Label style={{ minWidth: "7rem", textAlign: "center" }} size="large">
                {slotInfo.scheduledCount} / {slotInfo.totalSlots}
              </Label>
            </Form.Field>
            <Form.Field>
              <span>Slot(s)</span>
            </Form.Field>
          </Form.Group>

          <Form.Group style={{ alignItems: "center", marginBottom: "1.5rem" }}>
            <Form.Field>
              <span style={{ fontSize: "1.05rem", fontWeight: "600" }}>
                {props.event.full_date}
              </span>
            </Form.Field>

            <Form.Field>
              <TimeComboBoxStatic
                style={{ width: "6.75rem" }}
                disabled={slotInfo.scheduledCount}
                interval={15}
                maxHour={timeToDecimal(props.dsb.doctor_dsb_end_time)}
                minHour={timeToDecimal(props.dsb.doctor_dsb_start_time)}
                value={formData.start_time}
                onTextChange={handleChangeTimeSlot("start_time")}
              />
            </Form.Field>

            <Form.Field>
              <span style={{ fontSize: "1.25rem", fontWeight: "500" }}>-</span>
            </Form.Field>

            <Form.Field>
              <TimeComboBoxStatic
                style={{ width: "6.75rem" }}
                disabled={slotInfo.scheduledCount}
                interval={15}
                maxHour={timeToDecimal(props.dsb.doctor_dsb_end_time)}
                minHour={timeToDecimal(props.dsb.doctor_dsb_start_time)}
                value={formData.end_time}
                onTextChange={handleChangeTimeSlot("end_time")}
              />
            </Form.Field>
          </Form.Group>

          <Form.Group style={{ alignItems: "center", marginBottom: "1.5rem" }}>
            <Form.Field>
              <label style={{ fontSize: "1.1rem", fontWeight: "500" }}>Service time/Slot</label>
            </Form.Field>

            <Form.Field style={{ margin: 0, width: "8rem" }}>
              <Input
                disabled={slotInfo.scheduledCount}
                type="number"
                value={formData.slot_length}
                fluid
                onChange={(e, data) => {
                  handleChangeValue("slot_length", data.value);
                }}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (/^[e\-+.]$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Field>

            <Form.Field>
              <span style={{ fontSize: "1.1rem" }}>min</span>
            </Form.Field>
          </Form.Group>

          {/* OFF Checkbox */}
          <Form.Field style={{ marginBottom: "2rem" }}>
            <Checkbox
              checked={formData.inactive}
              label={<label style={{ fontSize: "1.1rem", fontWeight: "500" }}>OFF</label>}
              onChange={(e, data) => {
                handleChangeValue("inactive", !!data.checked);
              }}
            />
          </Form.Field>
        </Form>

        <div
          style={{
            display: "flex",
            gap: "1rem",
            justifyContent: "center",
            paddingBottom: "0.5rem",
          }}
        >
          {slotInfo.scheduledCount === 0 && (
            <Button
              style={{
                backgroundColor: "rgba(125, 125, 125, 1)",
                borderRadius: "6px",
                color: "white",
              }}
              onClick={handleDelete}
            >
              Delete Schedule
            </Button>
          )}

          <Button
            style={{
              backgroundColor: "var(--primary-theme-color)",
              borderRadius: "6px",
              color: "white",
            }}
            loading={isLoading}
            onClick={() => {
              void handleSave();
            }}
          >
            SAVE
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ModEditSchedule;
